import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { getITLocations, createITLocation, updateITLocation, deleteITLocation, getClients } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { MapPin, Plus, PencilSimple, Trash, MagnifyingGlass, Buildings } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EMPTY_LOCATION = {
  client_id: '', name: '', address: '', city: '', state_province: '',
  country: 'Panamá', zip_code: '', contact_name: '', contact_phone: '',
  contact_email: '', areas: [], notes: ''
};

export default function ClientLocationsPage() {
  const [locations, setLocations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLoc, setEditingLoc] = useState(null);
  const [form, setForm] = useState(EMPTY_LOCATION);
  const [filterClient, setFilterClient] = useState('all');
  const [clients, setClients] = useState([]);
  const [areasText, setAreasText] = useState('');

  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterClient && filterClient !== 'all') params.client_id = filterClient;
      const res = await getITLocations(params);
      const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setLocations(list);
      setTotal(res.data?.total ?? list.length);
    } catch (err) {
      toast.error('Error loading locations');
    } finally {
      setLoading(false);
    }
  }, [filterClient]);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  useEffect(() => {
    getClients().then(res => setClients(res.data || [])).catch(() => {});
  }, []);

  const handleCreate = () => {
    setEditingLoc(null);
    setForm(EMPTY_LOCATION);
    setAreasText('');
    setShowModal(true);
  };

  const handleEdit = (loc) => {
    setEditingLoc(loc);
    setForm({ ...EMPTY_LOCATION, ...loc });
    setAreasText((loc.areas || []).join(', '));
    setShowModal(true);
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`¿Eliminar la ubicación "${loc.name}"?`)) return;
    try {
      await deleteITLocation(loc._id);
      toast.success('Ubicación eliminada');
      loadLocations();
    } catch (err) {
      toast.error('Error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...form, areas: areasText.split(',').map(a => a.trim()).filter(Boolean) };
    try {
      if (editingLoc) {
        await updateITLocation(editingLoc._id, data);
        toast.success('Ubicación actualizada');
      } else {
        await createITLocation(data);
        toast.success('Ubicación creada');
      }
      setShowModal(false);
      loadLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <MapPin className="text-[#3AB8D6]" size={28} />
              Ubicaciones de Cliente
            </h1>
            <p className="text-gray-400 text-sm mt-1">Sucursales, sedes y ubicaciones por cliente</p>
          </div>
          <Button onClick={handleCreate} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
            <Plus size={18} className="mr-2" /> Nueva Ubicación
          </Button>
        </div>

        <div className="flex gap-3">
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="w-[220px] bg-[#1a1d23] border-gray-700 text-white">
              <SelectValue placeholder="Filtrar por cliente" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1d23] border-gray-700">
              <SelectItem value="all">Todos los clientes</SelectItem>
              {clients.map(c => (
                <SelectItem key={c._id} value={c._id}>{c.name || c.company}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-[#1a1d23] border border-gray-800 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E2B05E]"></div>
            </div>
          ) : locations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MapPin size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay ubicaciones registradas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800 text-xs text-gray-400 uppercase">
                    <th className="text-left p-3">Nombre</th>
                    <th className="text-left p-3">Cliente</th>
                    <th className="text-left p-3">Dirección</th>
                    <th className="text-left p-3">Ciudad</th>
                    <th className="text-left p-3">Contacto</th>
                    <th className="text-left p-3">Áreas</th>
                    <th className="text-right p-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.map((loc) => (
                    <tr key={loc._id} className="border-b border-gray-800/50 hover:bg-[#12141a]">
                      <td className="p-3 text-white font-medium text-sm">{loc.name}</td>
                      <td className="p-3 text-gray-300 text-sm">{loc.client_name || '-'}</td>
                      <td className="p-3 text-gray-400 text-sm">{loc.address || '-'}</td>
                      <td className="p-3 text-gray-400 text-sm">{loc.city || '-'}</td>
                      <td className="p-3 text-gray-400 text-sm">
                        {loc.contact_name && <div>{loc.contact_name}</div>}
                        {loc.contact_phone && <div className="text-xs">{loc.contact_phone}</div>}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(loc.areas || []).map((area, i) => (
                            <Badge key={i} className="text-xs bg-[#3AB8D6]/20 text-[#3AB8D6]">{area}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(loc)}>
                            <PencilSimple size={16} className="text-[#E2B05E]" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(loc)}>
                            <Trash size={16} className="text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-[#E2B05E]">
                {editingLoc ? 'Editar Ubicación' : 'Nueva Ubicación'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Cliente *</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm(prev => ({ ...prev, client_id: v }))}>
                    <SelectTrigger className="bg-[#12141a] border-gray-700 text-white">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {clients.map(c => (
                        <SelectItem key={c._id} value={c._id}>{c.name || c.company}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Nombre *</Label>
                  <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" placeholder="Ej: Sede Principal" />
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-300">Dirección</Label>
                  <Input value={form.address} onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Ciudad</Label>
                  <Input value={form.city} onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">País</Label>
                  <Input value={form.country} onChange={(e) => setForm(prev => ({ ...prev, country: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Contacto</Label>
                  <Input value={form.contact_name} onChange={(e) => setForm(prev => ({ ...prev, contact_name: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Teléfono Contacto</Label>
                  <Input value={form.contact_phone} onChange={(e) => setForm(prev => ({ ...prev, contact_phone: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" />
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-300">Áreas (separadas por coma)</Label>
                  <Input value={areasText} onChange={(e) => setAreasText(e.target.value)} className="bg-[#12141a] border-gray-700 text-white" placeholder="Ej: Recepción, IT, Contabilidad, Gerencia" />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Notas</Label>
                <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} className="bg-[#12141a] border-gray-700 text-white" rows={2} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-gray-600 text-gray-300">Cancelar</Button>
                <Button type="submit" className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
                  {editingLoc ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
